import { toDecimal } from '@mm/shared';
import clsx from 'clsx';
import type { ReactNode } from 'react';
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Legend,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { Link } from 'react-router-dom';
import { Badge, Card } from '../../components/ui/display';
import { ForeignAmounts } from '../../components/ForeignAmounts';
import type { ForeignAmount, PnlFigures } from '../../types/models';
import { formatDecimal, formatMoney, formatPercent } from '../../utils/format';

/** ₹1.2Cr / ₹3.4L / ₹12k axis labels. */
export const compactInr = (v: number) => {
  const a = Math.abs(v);
  const sign = v < 0 ? '-' : '';
  return a >= 1e7
    ? `${sign}₹${(a / 1e7).toFixed(1)}Cr`
    : a >= 1e5
      ? `${sign}₹${(a / 1e5).toFixed(1)}L`
      : a >= 1e3
        ? `${sign}₹${(a / 1e3).toFixed(0)}k`
        : `${sign}₹${a}`;
};

export const roasText = (v: string | null) => (v == null ? '—' : `${formatDecimal(v, 2)}×`);
const isLoss = (v: string | null | undefined) => !!v && toDecimal(v).isNegative();
const isZero = (v: string | null | undefined) => !v || toDecimal(v).isZero();

const MONTH_NAMES = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
];
/** '2026-03' → 'Mar 2026' */
export const monthLabel = (ym: string) =>
  `${MONTH_NAMES[Number(ym.slice(5, 7)) - 1]} ${ym.slice(0, 4)}`;

/** Profit in green, loss in red. */
export function ProfitText({ value, strong }: { value: string; strong?: boolean }) {
  return (
    <span
      className={clsx(
        'num whitespace-nowrap',
        strong && 'font-semibold',
        isZero(value) ? 'text-slate-500' : isLoss(value) ? 'text-rose-700' : 'text-emerald-700',
      )}
    >
      {formatMoney(value)}
    </span>
  );
}

/**
 * Headline figures for a range: the ads P&L (ads spend vs revenue, ROAS, ads
 * ROI) and, separately, the final net profit after other expenses (with ROI).
 * Marketing comes before revenue everywhere.
 */
export function PnlSummary({
  f,
  revenueForeign,
  adjustmentNote,
}: {
  f: PnlFigures | undefined;
  /** Revenue in its own currency, shown under the INR revenue. */
  revenueForeign?: ForeignAmount[];
  adjustmentNote?: ReactNode;
}) {
  const items: { label: string; value: ReactNode; sub?: ReactNode; strong?: boolean }[] = [
    {
      label: 'Ads spend (before GST)',
      value: formatMoney(f?.marketingExpense),
      sub: f ? `GST ${formatMoney(f.marketingGst)}` : undefined,
    },
    {
      label: 'Revenue',
      value: formatMoney(f?.revenue),
      sub: revenueForeign?.length ? <ForeignAmounts items={revenueForeign} /> : undefined,
    },
    {
      label: 'Ads profit (revenue − ads)',
      value: f ? <ProfitText value={f.adsProfit} /> : '—',
      sub: f ? `ROAS ${roasText(f.roas)} · ROI ${formatPercent(f.adsRoiPercent)}` : undefined,
    },
    {
      label: 'Other expenses',
      value: formatMoney(f?.otherExpense),
      sub: f ? 'Salary, servers, software…' : undefined,
    },
    {
      label: 'Net profit (after other expenses)',
      value: f ? <ProfitText value={f.operatingProfit} /> : '—',
      sub: f ? `ROI ${formatPercent(f.roiPercent)}` : undefined,
      strong: true,
    },
    {
      label: 'Cash outflow',
      value: formatMoney(f?.cashOutflow),
      sub: f ? `GST ${formatMoney(f.totalGst)}` : undefined,
    },
  ];
  return (
    <div className="mb-6">
      <div className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-6">
        {items.map((i) => (
          <div
            key={i.label}
            className={clsx(
              'rounded-xl border bg-white p-4 shadow-sm',
              i.strong ? 'border-brand-200 ring-1 ring-brand-100' : 'border-slate-200',
            )}
          >
            <p className="text-xs font-medium text-slate-500">{i.label}</p>
            <p className="num mt-1 text-lg font-semibold text-slate-900">{i.value}</p>
            {i.sub && <div className="num mt-0.5 text-xs text-slate-400">{i.sub}</div>}
          </div>
        ))}
      </div>
      {adjustmentNote && <p className="mt-2 text-xs text-slate-500">{adjustmentNote}</p>}
    </div>
  );
}

export interface ChartPoint {
  label: string;
  revenue: number;
  expense: number;
  profit: number;
}

/** Spend and revenue as bars, profit as a line (ads only, or net of all expenses). */
export function PnlChart({
  data,
  height = 300,
  mode = 'full',
}: {
  data: ChartPoint[];
  height?: number;
  mode?: 'ads' | 'full';
}) {
  return (
    <div style={{ height }}>
      <ResponsiveContainer width="100%" height="100%">
        <ComposedChart data={data} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
          <XAxis
            dataKey="label"
            tick={{ fontSize: 11, fill: '#64748b' }}
            tickLine={false}
            axisLine={false}
          />
          <YAxis
            tickFormatter={compactInr}
            tick={{ fontSize: 11, fill: '#64748b' }}
            tickLine={false}
            axisLine={false}
            width={64}
          />
          <Tooltip
            formatter={(v, name) => [formatMoney(String(v)), String(name)]}
            contentStyle={{ borderRadius: 8, borderColor: '#e2e8f0', fontSize: 12 }}
          />
          <Legend wrapperStyle={{ fontSize: 12 }} />
          <Bar
            dataKey="expense"
            name={mode === 'ads' ? 'Ads spend' : 'Ads + other expenses'}
            fill="#94a3b8"
            radius={[3, 3, 0, 0]}
            maxBarSize={40}
          />
          <Bar
            dataKey="revenue"
            name="Revenue"
            fill="#10b981"
            radius={[3, 3, 0, 0]}
            maxBarSize={40}
          />
          <Line
            dataKey="profit"
            name={mode === 'ads' ? 'Ads profit' : 'Net profit'}
            stroke="#4f46e5"
            strokeWidth={2}
            dot={data.length <= 40}
            type="linear"
          />
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
}

export const toPoint = (
  label: string,
  f: PnlFigures,
  mode: 'ads' | 'full' = 'full',
): ChartPoint => ({
  label,
  revenue: Number(f.revenue),
  expense: Number(mode === 'ads' ? f.marketingExpense : f.operatingExpense),
  profit: Number(mode === 'ads' ? f.adsProfit : f.operatingProfit),
});

export interface PnlRow {
  key: string;
  label: ReactNode;
  f: PnlFigures;
  adjusted?: boolean;
  /** Highlight the row (e.g. Saturday / Sunday in the daily P&L). */
  weekend?: boolean;
  /** First row of a group (app-wise view): drawn with a stronger top border. */
  groupStart?: boolean;
  /** Revenue in its own currency for this row. */
  foreign?: ForeignAmount[];
}

interface Col {
  header: string;
  cls?: string;
  cell: (f: PnlFigures, foreign?: ForeignAmount[]) => ReactNode;
}
const COL = {
  revenue: {
    header: 'Revenue',
    cell: (f, foreign) => (
      <>
        {formatMoney(f.revenue)}
        <ForeignAmounts items={foreign} />
      </>
    ),
  },
  ads: { header: 'Ads spend', cell: (f) => formatMoney(f.marketingExpense) },
  adsProfit: { header: 'Ads profit', cell: (f) => <ProfitText value={f.adsProfit} strong /> },
  roas: { header: 'ROAS', cls: '', cell: (f) => roasText(f.roas) },
  adsRoi: {
    header: 'Ads ROI',
    cls: '',
    cell: (f) => formatPercent(f.adsRoiPercent),
  },
  adsGst: {
    header: 'Ads GST',
    cls: 'text-violet-700',
    cell: (f) => formatMoney(f.marketingGst),
  },
  adsTotal: {
    header: 'Ads incl. GST',
    cls: '',
    cell: (f) => formatMoney(f.marketingTotal),
  },
  other: {
    header: 'Other exp.',
    cls: '',
    cell: (f) => formatMoney(f.otherExpense),
  },
  net: { header: 'Net profit', cell: (f) => <ProfitText value={f.operatingProfit} strong /> },
  roi: {
    header: 'ROI',
    cls: '',
    cell: (f) => formatPercent(f.roiPercent),
  },
  gst: {
    header: 'GST',
    cls: 'text-violet-700',
    cell: (f) => formatMoney(f.totalGst),
  },
  cash: {
    header: 'Cash outflow',
    cls: '',
    cell: (f) => formatMoney(f.cashOutflow),
  },
} satisfies Record<string, Col>;

/**
 * P&L table, one row per period / app plus totals; ads spend comes before revenue.
 *  - mode "ads" (daily): ads spend vs revenue only; other expenses are not
 *    spread over days (they are listed in their own card);
 *  - mode "full" (months, years, apps): also other expenses, net profit and ROI.
 */
export function PnlTable({
  rows,
  total,
  totalForeign,
  firstHeader,
  mode = 'full',
}: {
  rows: PnlRow[];
  total?: PnlFigures;
  /** Revenue in its own currency for the total row. */
  totalForeign?: ForeignAmount[];
  firstHeader: string;
  mode?: 'ads' | 'full';
}) {
  const cols: Col[] =
    mode === 'ads'
      ? [COL.ads, COL.revenue, COL.adsProfit, COL.roas, COL.adsRoi, COL.adsGst, COL.adsTotal]
      : [
          COL.ads,
          COL.revenue,
          COL.adsProfit,
          COL.roas,
          COL.other,
          COL.net,
          COL.roi,
          COL.gst,
          COL.cash,
        ];
  const cells = (f: PnlFigures, foreign?: ForeignAmount[]) =>
    cols.map((c) => (
      <td key={c.header} className={clsx('num px-4 py-2 text-right whitespace-nowrap', c.cls)}>
        {c.cell(f, foreign)}
      </td>
    ));
  return (
    <div className="relative overflow-x-auto">
      <table className="min-w-full text-sm">
        <thead className="bg-slate-50 text-xs font-semibold tracking-wide text-slate-500 uppercase">
          <tr>
            <th className="px-4 py-3 text-left">{firstHeader}</th>
            {cols.map((c) => (
              <th key={c.header} className={clsx('px-4 py-3 text-right', c.cls)}>
                {c.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {rows.map((r) => (
            <tr
              key={r.key}
              className={clsx(
                r.weekend ? 'bg-amber-50/70 hover:bg-amber-50' : 'hover:bg-slate-50/60',
                r.groupStart && 'border-t-2 border-slate-200',
              )}
            >
              <td className="px-4 py-2 whitespace-nowrap">
                <span className="flex items-center gap-2">
                  {r.label}
                  {r.adjusted && <Badge tone="violet">actuals</Badge>}
                </span>
              </td>
              {cells(r.f, r.foreign)}
            </tr>
          ))}
        </tbody>
        {total && (
          <tfoot className="border-t-2 border-slate-200 bg-slate-50/60 font-semibold">
            <tr>
              <td className="px-4 py-2">Total</td>
              {cells(total, totalForeign)}
            </tr>
          </tfoot>
        )}
      </table>
    </div>
  );
}

export interface OtherExpensesBreakdown {
  months: string[];
  total: string;
  gst: string;
  categories: {
    category: { id: string; name: string; code: string };
    amount: string;
    gst: string;
    months: Record<string, string>;
  }[];
}

/** Other expenses of the range by category (and month), kept apart from the daily ads P&L. */
export function OtherExpensesCard({
  data,
  startDate,
  endDate,
}: {
  data: OtherExpensesBreakdown;
  startDate: string;
  endDate: string;
}) {
  const showMonths = data.months.length > 1 && data.months.length <= 12;
  return (
    <Card
      title="Other expenses"
      description="Salary, servers, software… for the whole period. Not spread over days; included in net profit."
      bodyClassName="p-0"
      actions={
        <Link
          to={`/expenses?preset=custom&start=${startDate}&end=${endDate}`}
          className="text-sm text-brand-700 hover:underline"
        >
          View entries
        </Link>
      }
    >
      {data.categories.length === 0 ? (
        <p className="px-5 py-4 text-sm text-slate-500">No other expenses in this period.</p>
      ) : (
        <div className="relative overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-slate-50 text-xs font-semibold tracking-wide text-slate-500 uppercase">
              <tr>
                <th className="px-4 py-3 text-left">Category</th>
                {showMonths &&
                  data.months.map((m) => (
                    <th key={m} className="px-4 py-3 text-right">
                      {monthLabel(m)}
                    </th>
                  ))}
                <th className="px-4 py-3 text-right">Total</th>
                <th className="px-4 py-3 text-right text-violet-700">GST</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {data.categories.map((c) => (
                <tr key={c.category.id}>
                  <td className="px-4 py-2 font-medium text-slate-900">{c.category.name}</td>
                  {showMonths &&
                    data.months.map((m) => (
                      <td key={m} className="num px-4 py-2 text-right">
                        {c.months[m] ? (
                          formatMoney(c.months[m])
                        ) : (
                          <span className="text-slate-300">—</span>
                        )}
                      </td>
                    ))}
                  <td className="num px-4 py-2 text-right font-medium">{formatMoney(c.amount)}</td>
                  <td className="num px-4 py-2 text-right text-violet-700">{formatMoney(c.gst)}</td>
                </tr>
              ))}
            </tbody>
            <tfoot className="border-t-2 border-slate-200 bg-slate-50/60 font-semibold">
              <tr>
                <td className="px-4 py-2">Total</td>
                {showMonths && data.months.map((m) => <td key={m} />)}
                <td className="num px-4 py-2 text-right">{formatMoney(data.total)}</td>
                <td className="num px-4 py-2 text-right text-violet-700">
                  {formatMoney(data.gst)}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}
    </Card>
  );
}
